import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import LoginScreen from './LoginScreen';
import MoodCheckIn from './MoodCheckIn';
import KidDashboard from './KidDashboard';
import ParentApp from './ParentApp';
import OnboardingFlow from './OnboardingFlow';
import { Spinner } from './UI';

export default function AppShell() {
  const [mode, setMode] = useState("loading");
  const [parent, setParent] = useState(null);
  const [kid, setKid] = useState(null);
  const [children, setChildren] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [rewards, setRewards] = useState([]);

  // Check for existing session on load
  useEffect(() => {
    const checkSession = async () => {
      // Check for remembered kid first
      const rememberedKid = localStorage.getItem('thrivetrack_kid');
      if (rememberedKid) {
        try {
          const kidData = JSON.parse(rememberedKid);
          setKid(kidData);
          await loadKidData(kidData.id, kidData.parent_id);
          const today = new Date().toDateString();
          if (kidData.last_mood_date !== today) {
            setMode("kidMood");
          } else {
            setMode("kid");
          }
          return;
        } catch (e) {
          localStorage.removeItem('thrivetrack_kid');
        }
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: parentData, error } = await supabase.from('parents').select('*').eq('id', session.user.id).single();
        if (parentData && !error) {
          setParent(parentData);
          setMode("parent");
          return;
        }
        await supabase.auth.signOut();
      }
      setMode("login");
    };
    checkSession();
  }, []);

  const loadParentData = useCallback(async (parentId) => {
    const today = new Date().toISOString().split('T')[0];
    const [{ data: kids }, { data: taskData }, { data: rewardData }] = await Promise.all([
      supabase.from('children').select('*').eq('parent_id', parentId).order('created_at'),
      supabase.from('tasks').select('*').eq('parent_id', parentId).order('created_at'),
      supabase.from('rewards').select('*').eq('parent_id', parentId).order('created_at'),
    ]);

    // Compute Monday 00:00 UTC of the current week (ISO week = Mon–Sun)
    const nowUTC = new Date();
    const daysSinceMonday = (nowUTC.getUTCDay() + 6) % 7; // Sun=6, Mon=0, …, Sat=5
    const mondayUTC = new Date(nowUTC);
    mondayUTC.setUTCDate(nowUTC.getUTCDate() - daysSinceMonday);
    mondayUTC.setUTCHours(0, 0, 0, 0);
    const mondayStr = mondayUTC.toISOString().split('T')[0];

    // Reset completed Daily tasks that were last updated before today (UTC)
    const dailyTasksToReset = (taskData || []).filter(t =>
      t.frequency === 'Daily' &&
      t.status === 'completed' &&
      t.updated_at &&
      t.updated_at.split('T')[0] < today
    );

    // Reset completed OR pending Weekly tasks last updated before this Monday (UTC)
    // Both statuses reset so each new week starts fresh regardless of approval state
    const weeklyTasksToReset = (taskData || []).filter(t =>
      t.frequency === 'Weekly' &&
      (t.status === 'completed' || t.status === 'pending') &&
      t.updated_at &&
      t.updated_at.split('T')[0] < mondayStr
    );

    const tasksToReset = [...dailyTasksToReset, ...weeklyTasksToReset];
    if (tasksToReset.length > 0) {
      await Promise.all(tasksToReset.map(t =>
        supabase.from('tasks').update({ status: 'active' }).eq('id', t.id)
      ));
      const { data: refreshedTasks } = await supabase.from('tasks').select('*').eq('parent_id', parentId).order('created_at');
      setTasks(refreshedTasks || []);
    } else {
      setTasks(taskData || []);
    }
    setChildren(kids || []);
    setRewards(rewardData || []);
  }, []);

  useEffect(() => {
    if (parent) loadParentData(parent.id);
  }, [parent, loadParentData]);

  const loadKidData = useCallback(async (kidId, parentId) => {
    const [{ data: taskData }, { data: rewardData }] = await Promise.all([
      supabase.from('tasks').select('*').eq('child_id', kidId).order('created_at'),
      supabase.from('rewards').select('*').eq('child_id', kidId).order('created_at'),
    ]);
    setTasks(taskData || []);
    setRewards(rewardData || []);
  }, []);

  const handleParentLogin = async (parentData) => {
    setParent(parentData);
    setMode("parent");
  };

  const handleKidLogin = async (kidData) => {
    setKid(kidData);
    localStorage.setItem('thrivetrack_kid', JSON.stringify(kidData));
    await loadKidData(kidData.id, kidData.parent_id);
    const today = new Date().toDateString();
    if (kidData.last_mood_date !== today) {
      setMode("kidMood");
    } else {
      setMode("kid");
    }
  };

  const handleMoodComplete = async () => {
    const today = new Date().toDateString();
    await supabase.from('children').update({ last_mood_date: today }).eq('id', kid.id);
    setKid(prev => ({ ...prev, last_mood_date: today }));
    setMode("kid");
  };

  const handleTaskDone = (task) => {
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'pending' } : t));
  };

  const handleParentLogout = async () => {
    await supabase.auth.signOut();
    setParent(null);
    setChildren([]);
    setTasks([]);
    setRewards([]);
    setMode("login");
  };

  if (mode === "loading") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F8FDF9" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 36, color: "#2D6A4F", marginBottom: 20 }}>ThriveTrack</div>
          <Spinner />
        </div>
      </div>
    );
  }

  if (mode === "login") return <LoginScreen onParentLogin={handleParentLogin} onKidLogin={handleKidLogin} />;
  if (mode === "kidMood") return <MoodCheckIn child={kid} onComplete={handleMoodComplete} onSwitchKid={() => { localStorage.removeItem('thrivetrack_kid'); setKid(null); setTasks([]); setRewards([]); setMode("login"); }} />;
  if (mode === "kid") return <KidDashboard child={kid} tasks={tasks} rewards={rewards} onTaskDone={handleTaskDone} onLogout={() => { localStorage.removeItem('thrivetrack_kid'); setKid(null); setTasks([]); setRewards([]); setMode("login"); }} />;
  if (parent && !parent.onboarding_complete) {
    console.log("Onboarding parent object:", parent);
    return (
      <OnboardingFlow
        parent={parent}
        onComplete={() => setParent({ ...parent, onboarding_complete: true })}
      />
    );
  }

  if (mode === "parent") return (
    <ParentApp
      parent={parent}
      onLogout={handleParentLogout}
      children={children}
      tasks={tasks}
      rewards={rewards}
      onDataChange={() => loadParentData(parent.id)}
    />
  );

  return null;
}
